import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, X, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Reveal, SectionTag, BigHead } from "./Reveal";
import { MaintenanceTiers } from "./Maintenance";
import { BrandPackagesBody } from "./BrandPackages";
import { openContact } from "./ContactDialog";

const SERVICES = [
  { n: "01", title: "Branding", desc: "Logos, visual identity, brand systems, and brand guidelines.", bg: "bg-staxyellow", text: "text-ink", brand: true },
  { n: "02", title: "Graphic Design", desc: "Print materials, social graphics, marketing collateral, presentations, and promotional materials.", bg: "bg-staxmagenta", text: "text-white" },
  { n: "03", title: "Website Design", desc: "Strategic, visually engaging websites that turn visitors into customers.", bg: "bg-staxcyan", text: "text-ink", care: true },
  { n: "04", title: "Marketing", desc: "Creative marketing materials and campaigns designed to help brands grow.", bg: "bg-white", text: "text-ink" },
  { n: "05", title: "Social Media Design", desc: "Consistent, recognizable visuals that make your brand stand out online.", bg: "bg-ink", text: "text-white" },
  { n: "06", title: "Print Design", desc: "Business cards, flyers, brochures, signage, packaging, and other physical brand experiences.", bg: "bg-staxyellow", text: "text-ink" },
];

export const Services = ({ ch = "02" }) => {
  const [careOpen, setCareOpen] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);

  return (
    <section id="services" data-testid="services-section" className="relative overflow-hidden bg-ink py-24 sm:py-32">
      <div className="ink-dot absolute -right-32 -top-32 h-96 w-96 bg-staxcyan opacity-80" />
      <div className="ink-dot absolute -left-24 bottom-10 h-64 w-64 bg-staxyellow opacity-80" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8">
        <Reveal>
          <SectionTag color="bg-staxmagenta text-white border-white" ch={ch}>Services</SectionTag>
        </Reveal>
        <div className="mt-6 flex flex-wrap items-end justify-between gap-6">
          <Reveal delay={0.08}>
            <BigHead className="text-white">
              What<br />I Do<span className="text-staxyellow">.</span>
            </BigHead>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="max-w-sm text-white/60 font-medium text-base">
              Full-stack creativity — from the first logo sketch to the campaign that launches it.
              Website projects come with optional monthly care plans.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-4 border-white/90">
          {SERVICES.map((s, i) => (
            <motion.button
              key={s.n}
              data-testid={`service-card-${s.n}`}
              onClick={() => (s.care ? setCareOpen(true) : s.brand ? setBrandOpen(true) : openContact(`Hi! I'm interested in ${s.title}.`))}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
              className={`group relative border-2 border-white/90 ${s.bg} ${s.text} p-8 text-left min-h-[240px] flex flex-col justify-between transition-transform duration-300 hover:-translate-y-1.5 hover:z-10 hover:shadow-[8px_8px_0_var(--sm)]`}
            >
              <div className="flex items-start justify-between">
                <span className="font-mono2 text-xs font-bold tracking-[0.3em]">{s.n}</span>
                <ArrowUpRight size={22} className="opacity-0 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </div>
              <div>
                <h3 className="font-display uppercase text-3xl leading-none">{s.title}</h3>
                <p className="mt-3 text-sm font-medium opacity-75 leading-relaxed">{s.desc}</p>
                {s.care && (
                  <span className="mt-4 inline-block border-2 border-ink bg-white px-2.5 py-1 font-mono2 text-[10px] font-bold uppercase tracking-[0.2em] text-ink">
                    + Care plans inside
                  </span>
                )}
                {s.brand && (
                  <span className="mt-4 inline-block border-2 border-ink bg-white px-2.5 py-1 font-mono2 text-[10px] font-bold uppercase tracking-[0.2em] text-ink">
                    + Packages inside
                  </span>
                )}
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <Dialog open={careOpen} onOpenChange={setCareOpen}>
        <DialogContent
          data-testid="web-care-modal"
          data-lenis-prevent
          className="border-4 border-ink bg-paper p-0 sm:max-w-5xl rounded-none shadow-hard gap-0 max-h-[90vh] overflow-y-auto [&>button]:hidden"
        >
          <div className="flex items-center justify-between border-b-4 border-ink bg-staxcyan px-6 py-4 sticky top-0 z-10">
            <DialogTitle className="font-display uppercase text-xl text-ink">Website Design + Care Plans</DialogTitle>
            <button data-testid="web-care-close" onClick={() => setCareOpen(false)} aria-label="Close"
              className="border-2 border-ink bg-white p-1 hover:bg-staxmagenta hover:text-white transition-colors">
              <X size={18} />
            </button>
          </div>
          <div className="p-6 sm:p-8">
            <p className="max-w-xl text-base font-medium text-ink/70">
              Every STAX website can stay fast, secure, and fresh with a monthly plan.
              Pick a tier — or just the website.
            </p>
            <div className="mt-8">
              <MaintenanceTiers onSelect={() => setCareOpen(false)} />
            </div>
            <button
              data-testid="web-care-just-site"
              onClick={() => { setCareOpen(false); openContact("Hi! I'm interested in Website Design."); }}
              className="group mt-8 inline-flex items-center gap-2 border-2 border-ink bg-ink px-6 py-3.5 font-display uppercase text-sm text-white transition-transform hover:-translate-y-0.5"
            >
              Just the website, thanks <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={brandOpen} onOpenChange={setBrandOpen}>
        <DialogContent
          data-testid="branding-packages-modal"
          data-lenis-prevent
          className="border-4 border-ink bg-paper p-0 sm:max-w-6xl rounded-none shadow-hard gap-0 max-h-[90vh] overflow-y-auto [&>button]:hidden"
        >
          <div className="flex items-center justify-between border-b-4 border-ink bg-staxyellow px-6 py-4 sticky top-0 z-10">
            <DialogTitle className="font-display uppercase text-xl text-ink">Branding Packages</DialogTitle>
            <button data-testid="branding-packages-close" onClick={() => setBrandOpen(false)} aria-label="Close"
              className="border-2 border-ink bg-white p-1 hover:bg-staxmagenta hover:text-white transition-colors">
              <X size={18} />
            </button>
          </div>
          <div className="p-6 sm:p-8">
            <BrandPackagesBody onAction={() => setBrandOpen(false)} />
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};
